import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ReactApexCharts from 'react-apexcharts';

import { api } from '../../api';
import { formatDate } from '../../util';

interface HourInOutTotal {
  hour: number;
  total: number;
}
interface PersonTypeInOut {
  person_type_id: number;
  person_type: string;
  hour_total_list: HourInOutTotal[];
}
interface ChartInOutProps {
  filterID: string;
  filterName: string;
  filterGateIDList: number[];
  filterAreaIDList: number[];
  filterCompanyIDList: number[];
  filterPersonTypeIDList: string[];
  filterScanTypes: string;
  filterStartDate: Date | null;
  filterEndDate: Date | null;
}
const ChartInOut: React.FC<ChartInOutProps> = ({
  filterID,
  filterName,
  filterGateIDList,
  filterAreaIDList,
  filterCompanyIDList,
  filterPersonTypeIDList,
  filterScanTypes,
  filterStartDate,
  filterEndDate
}: ChartInOutProps) => {
  const [personTypeInOuts, setPersonTypeInOuts] = useState<PersonTypeInOut[]>([
    {
      person_type: 'ไม่มีข้อมูล',
      person_type_id: -1,
      hour_total_list: []
    }
  ]);

  const chartSeries = useMemo(() => {
    return personTypeInOuts.map((personTypeInOut) => {
      const hourTotalList: number[] = new Array(24).fill(0);
      personTypeInOut.hour_total_list.forEach((data) => {
        if (data.hour <= 23 && data.hour >= 0) hourTotalList[(data.hour) % 24] = data.total;
      });
      return {
        name: personTypeInOut.person_type,
        data: hourTotalList
      };
    });
  }, [personTypeInOuts]);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', filterID);
    }
    if (filterName) {
      searchParams.set('name', filterName);
    }
    if (filterGateIDList) {
      filterGateIDList.forEach((gate_id) => {
        searchParams.append('gate_id', `${gate_id}`);
      });
    }
    if (filterAreaIDList) {
      filterAreaIDList.forEach((area_id) => {
        searchParams.append('area_id', `${area_id}`);
      });
    }
    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    if (filterScanTypes) {
      searchParams.set('type_id', `${filterScanTypes}`);
    }
    if (filterStartDate) {
      searchParams.set('start_date', `${formatDate(filterStartDate)}`);
    }
    if (filterEndDate) {
      searchParams.set('end_date', `${formatDate(filterEndDate)}`);
    }
    api.get<PersonTypeInOut[]>(`/in-out/graph`, { params: searchParams }).then((res) => {
      const { data } = res;
      setPersonTypeInOuts(data);
    });
  }, [
    filterID,
    filterName,
    filterGateIDList,
    filterAreaIDList,
    filterCompanyIDList,
    filterPersonTypeIDList,
    filterScanTypes,
    filterStartDate,
    filterEndDate
  ]);

  return (
    <Box>
      <Typography variant="h6" sx={{ p: 2 }}>
        จำนวนการแลกบัตรเข้าออกแต่ละประเภท
      </Typography>
      <Box>
        <ReactApexCharts
          series={chartSeries}
          options={{
            chart: {
              type: 'line',
              zoom: {
                enabled: false
              }
            },
            dataLabels: {
              enabled: false
            },
            stroke: {
              curve: 'straight'
            },
            grid: {
              row: {
                colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
                opacity: 0.5
              }
            },
            xaxis: {
              categories: new Array(24).fill(0).map((_, index) => `${index}:00`)
            }
          }}
          height={350}
        />
      </Box>
    </Box>
  );
};

export default ChartInOut;
